import { useCallback, useRef, useState, type ChangeEvent } from "react";

import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import Button from "@mui/material/Button";
import Dialog, { type DialogProps } from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useTranslation } from "react-i18next";

import { useAdvancedToggle } from "../../../hooks";

import type { PhrasesCardProps } from "./PhrasesCard";
import type { Phrase } from "./PhraseTextField";

type AddEditPhraseModalProps = ({ mode: "add"; phrase?: never } | { mode: "edit"; phrase: Phrase }) & {
  phrases: ReadonlyArray<Phrase>;
  onClose: () => void;
  onCreatePhrase: PhrasesCardProps["onCreatePhrase"];
  onUpdatePhrase: PhrasesCardProps["onUpdatePhrase"];
  saving?: boolean;
};

const AddEditPhraseModal = ({
  mode,
  phrase,
  phrases,
  onClose,
  onCreatePhrase,
  onUpdatePhrase,
  saving = false,
}: AddEditPhraseModalProps) => {
  const { t } = useTranslation(["common", "yup"]);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState(phrase?.name ?? "");
  const [bodyText, setBodyText] = useState(phrase?.bodyText ?? "");
  const [nameTouched, { toggleTrue: handleNameBlur }] = useAdvancedToggle(false);
  const [bodyTextTouched, { toggleTrue: handleBodyTextBlur }] = useAdvancedToggle(false);

  const trimmedName = name.trim();
  const nameRequiredError = (mode === "edit" || nameTouched) && !trimmedName;
  const bodyTextRequiredError = (mode === "edit" || bodyTextTouched) && !bodyText.trim();

  const duplicateName =
    !saving && phrases.some((p) => p.id !== phrase?.id && p.name.toLowerCase() === trimmedName.toLowerCase());

  const nameChanged = phrase !== undefined && trimmedName !== phrase.name;
  const bodyChanged = phrase !== undefined && bodyText !== phrase.bodyText;

  const canSave =
    Boolean(trimmedName && bodyText.trim()) && // neither name nor body text can be empty
    !duplicateName &&
    !saving &&
    (mode === "add" || nameChanged || bodyChanged); // must either be in add mode or something must have changed if in edit mode

  const handleNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => setName(event.target.value),
    [],
  );

  const handleBodyTextChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => setBodyText(event.target.value),
    [],
  );

  const handleSave = useCallback(async () => {
    if (!canSave) return;

    const saved = phrase
      ? await onUpdatePhrase({ id: phrase.id, name: trimmedName, bodyText })
      : await onCreatePhrase({ name: trimmedName, bodyText });

    if (saved) onClose();
  }, [bodyText, canSave, onCreatePhrase, onClose, phrase, trimmedName, onUpdatePhrase]);

  const nameHelperText = nameRequiredError
    ? t("yup:required")
    : duplicateName
    ? t("common:phrase_name_already_exists")
    : undefined;

  const handleDialogClose = useCallback<NonNullable<DialogProps["onClose"]>>(
    (_event, reason) => {
      if (saving || reason === "backdropClick") return;
      onClose();
    },
    [onClose, saving],
  );

  const handleDialogEntered = useCallback(() => {
    if (mode === "add") nameInputRef.current?.focus(); // focus name input when entering add modal
  }, [mode]);

  return (
    <Dialog
      open
      onClose={handleDialogClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="phrase-dialog-title"
      slotProps={{ transition: { onEntered: handleDialogEntered } }}
    >
      <DialogTitle id="phrase-dialog-title">
        {t(mode === "add" ? "common:add_phrase" : "common:edit_phrase")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            inputRef={nameInputRef}
            label={t("common:name")}
            placeholder={t("common:phrase_name")}
            value={name}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            disabled={saving}
            error={nameRequiredError || duplicateName}
            helperText={nameHelperText}
            fullWidth
          />
          <TextField
            label={t("common:body_text")}
            placeholder={t("common:phrase_body_text_placeholder")}
            value={bodyText}
            onChange={handleBodyTextChange}
            onBlur={handleBodyTextBlur}
            disabled={saving}
            error={bodyTextRequiredError}
            helperText={bodyTextRequiredError ? t("yup:required") : undefined}
            multiline
            minRows={4}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t("common:cancel")}
        </Button>
        <Button
          onClick={handleSave}
          disabled={!canSave}
          variant="contained"
          startIcon={mode === "add" ? <AddIcon /> : <SaveIcon />}
        >
          {t(mode === "add" ? "common:add" : "common:save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditPhraseModal;
